// @flow

import React from "react";
import { localize } from "redux-i18n";
import { SectionComponent, GSGrid, Chart, View } from "@components";
import type {
  ReportData,
  DepartmentProfileState,
} from "@modules/reporting/reducer/focus-lists/department/types";

type OwnProps = $Exact<{
  selectedDepartment: ReportData,
  departmentProfile: DepartmentProfileState,
  targetAbsenceRate: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export const FocusListDepartmentProfile = ({
  departmentProfile,
  selectedDepartment,
  t,
  targetAbsenceRate,
}: Props) => (
  <View gap={6} margin="1.5rem 0 0 0">
    {selectedDepartment && (
      <GSGrid
        height={82}
        columns={[
          {
            name: t("HEADCOUNT"),
            key: "headCount",
          },
          {
            name: t("ABSENCE_RATE"),
            key: "absenceRate",
          },
          {
            name: t("DAYS_LOST_PER_EMPLOYEE"),
            key: "daysLostPerEmployee",
          },
          {
            name: t("TOTAL_DAYS_LOST"),
            key: "totalDaysLost",
          },
          {
            name: t("TOTAL_ABSENCES_SPELLS"),
            key: "totalAbsences",
          },
          {
            name: t("TOTAL_ALERTS"),
            key: "totalAlerts",
          },
        ]}
        rows={[selectedDepartment]}
      />
    )}

    {departmentProfile && (
      <>
        <Chart
          type="LineChart"
          className="mt-4"
          title={t("ABSENCE_RATE_LAST_12_MONTHS")}
          values={[
            ["Period", t("ACTUAL_ABSENCE_RATE"), t("TARGET_ABSENCE_RATE")],
            ...[
              ...departmentProfile.absenceRatePerMonth.map(i => {
                return [i.description, i.value, targetAbsenceRate];
              }),
            ],
          ]}
        />
        <View>
          <Chart
            type="ColumnChart"
            className="mt-4"
            title={t("TOP_5_ABSENCE_REASONS_BY_WORKING_DAYS_LOST")}
            values={[
              ["Type", "", { role: "annotation" }],
              ...departmentProfile.top5AbsenceReasonsByWorkingDaysLost.map(item => [
                item.description,
                item.value,
                item.value,
              ]),
            ]}
            options={{
              legend: {
                position: "none",
              },
            }}
          />
        </View>
        <Chart
          type="ColumnChart"
          className="mt-4"
          title={t("TOP_10_EMPLOYEES_BY_WORKING_DAYS_LOST")}
          values={[
            ["Type", "", { role: "annotation" }],
            ...departmentProfile.top10EmployeesByWorkingDaysLost.map(item => [
              item.description,
              item.value,
              item.value,
            ]),
          ]}
          options={{
            legend: {
              position: "none",
            },
          }}
        />
        <View>
          <Chart
            type="ColumnChart"
            className="mt-4"
            title={t("TRENDS_ABSENCE_REASON_DLPE_BY_GENDER_TITLE")}
            values={[
              ["Type", "", { role: "annotation" }],
              ...departmentProfile.daysLostPerEmployeeByGender.map(item => [
                t(item.description),
                item.value,
                item.value,
              ]),
            ]}
            options={{
              legend: {
                position: "none",
              },
            }}
          />
        </View>
        <View>
          <Chart
            type="ColumnChart"
            className="mt-4"
            title={t("TRENDS_ABSENCE_REASON_DLPE_BY_AGE_GROUP_TITLE")}
            values={[
              ["Type", "", { role: "annotation" }],
              ...departmentProfile.daysLostPerEmployeeByAgeGroup.map(item => [
                t(item.description),
                item.value,
                item.value,
              ]),
            ]}
            options={{
              legend: {
                position: "none",
              },
            }}
          />
        </View>
      </>
    )}
  </View>
);

export default localize()(FocusListDepartmentProfile);
